import { Injectable, NgZone, inject } from '@angular/core';
import {SettingsData} from '../models/settings-data';
import {FirebaseApi} from '../../common/services/firebase-api.service';
import {Logger} from '../../common/utils/logger';
import {BehaviorSubject, Subject, Subscription} from 'rxjs';
import {UnitConverter} from '../utils/unit-converter';
import {LocalSettings} from '../models/local-settings';
import {CommonUtil} from '../../common/utils/common.util';
import {MeasureUnit} from '../enums/measure-unit.enum';
import {ActivatedRoute} from '@angular/router';
import {MapType} from '../enums/map-type';
import {AuthApiService} from '../../common/services/api/routes/auth-api.service';
import {ObjectUtil} from '../../common/utils/object.util';
import {ThemeId} from "../../common/enums/theme-id";
import {StringsService} from '../../../../projects/common/src/lib/services/strings.service';
import {AltitudeMode3d} from '../enums/altitude-mode.enum';
import {UpdateService} from '../../common/services/update.service';

/** Indexes into a per-system unit list; older settings stored them as strings ("0"). */
const UNIT_INDEX_KEYS = ['distanceUnit', 'areaUnit'];
/** Everything that changes how a measurement is written out. */
const UNIT_KEYS = ['measureUnitSystem', ...UNIT_INDEX_KEYS, 'showShortDistanceUnits', 'showShortAreaUnits',
  'showDistanceLabelDecimals'];
/** Whose settings are loaded when they are not a signed-in user's own document. */
const OWNER_IFRAME = '#iframe';
const OWNER_SIGNED_OUT = '#signed-out';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private route = inject(ActivatedRoute);
  private firebaseApi = inject(FirebaseApi);
  private authApi = inject(AuthApiService);
  private ngZone = inject(NgZone);


  private static sData = new SettingsData();
  private static sLocal = new LocalSettings();
  public data = new SettingsData();
  public local = new LocalSettings();
  onSettingsChanged = new BehaviorSubject<SettingsData>(null);
  onSettingsSaved = new BehaviorSubject<SettingsData>(this.data);
  onLocalSettingChanged = new BehaviorSubject(null);
  /**
   * The units changed with settings that arrived — loaded after the shapes,
   * or changed on another device. A change made here is announced by whoever
   * made it; this is for the ones nobody here made.
   */
  onUnitsReceived = new Subject<void>();

  private saveTimer;
  private subscription: Subscription;
  /** A user id, OWNER_IFRAME or OWNER_SIGNED_OUT: whose settings are loaded or loading. */
  private settingsOwner: string = null;
  /** Settings changed here and not written yet; the save writes these and nothing else. */
  private unsavedKeys = new Set<string>();
  /**
   * Local values for when the browser refuses site storage: an embed on a
   * site whose visitor blocks third-party data, or storage turned off. Reading
   * localStorage then throws, and it used to take the whole app down with it.
   */
  private memoryStorage = new Map<string, string>();

  constructor() {
    this.initLocalSettings();
    // A change still waiting out its debounce goes out before a reload for a
    // new version. Never inside an embed, which keeps its changes to itself (saveData).
    inject(UpdateService).beforeReload(() => CommonUtil.inIframe() ? Promise.resolve() : this.saveNow());
    // Measurements are written in the app's language, like the exports, not the browser's.
    inject(StringsService).getObservable().subscribe(language => {
      if (language) {
        UnitConverter.setLocale(language);
      }
    });
  }

  static get settings(): SettingsData {
    return this.sData;
  }

  static get localSettings(): LocalSettings {
    return this.sLocal;
  }

  initLocalSettings() {
    Logger.log('INIT Local Settings');
    ObjectUtil.every(this.local, (key, val) => {
      this.local[key] = this.getLocalValue(key, val);
    });
    if(this.getLocalValue('snapToPointFirst', true)){
      this.setLocalValue('snapToPoint', true);
      this.setLocalValue('snapToPointFirst', false);
    }
    this.setLocalValue('showTrafficLayer', false);
    this.setLocalValue('show3DMaps', false);
    this.setLocalValue('showParcels', false);
    this.setLocalValue('showBuildingsOverlays', false);
    // "At a fixed height" is no longer a choice (see fromFixedHeight). A
    // device that had it as its default draws shapes left on the default on
    // the ground; the shapes saved with it keep their height.
    if (this.local.default3dAltitudeMode === AltitudeMode3d.FIXED) {
      this.setLocalValue('default3dAltitudeMode', AltitudeMode3d.GROUND);
    }

    SettingsService.sLocal = this.local;
  }

  initCanvasSettings() {

    Logger.log('initCanvasSettings')
    const userId = this.firebaseApi.getUserId();
    const owner = CommonUtil.inIframe() ? OWNER_IFRAME : (userId || OWNER_SIGNED_OUT);
    // Claimed before anything is awaited: on a canvas opened directly, auth
    // and the canvas both get here before the first read comes back, and each
    // used to start its own listener.
    if (this.settingsOwner === owner) return;
    if (this.settingsOwner) {
      // Signed in as someone else: the listener and any change still waiting
      // to be saved belong to the previous account.
      this.stopListening();
      clearTimeout(this.saveTimer);
      this.unsavedKeys.clear();
    }
    this.settingsOwner = owner;

    if (owner === OWNER_IFRAME) {
      Logger.log('initCanvasSettings 1')

      this.getDefaultMeasureSystem().then(unit => {
        const iFrameSettings = new SettingsData();
        iFrameSettings.measureUnitSystem = unit;
        this.applyParamSettings(iFrameSettings)
        this.onNewSettings(iFrameSettings);
      });

    } else if (userId) {
      Logger.log('initCanvasSettings 2')
      return new Promise<void>(async (resolve) => {
        let foundSettings: boolean;
        try {
          foundSettings = (await this.firebaseApi.getSettingsDoc().ref.get()).exists;
        } catch (err) {
          Logger.error('Could not load the settings', err);
          // The next call tries again rather than finding them "loaded".
          if (this.settingsOwner === owner) {
            this.settingsOwner = null;
          }
          resolve();
          return;
        }
        if (this.settingsOwner !== owner) {
          // Another account signed in while this was being read.
          resolve();
          return;
        }
        this.subscription = this.firebaseApi.getSettingsDoc().valueChanges().subscribe({
          next: data => {
            if (data) {
              this.onNewSettings(data);
              this.saveUnitIndexesAsNumbers(data);
              resolve();
            }
          },
          // Refused once signed out, the listener is over: the next sign-in,
          // the same account's included, starts a new one rather than finding
          // the settings "loaded".
          error: err => {
            Logger.error('Settings listener stopped', err);
            if (this.settingsOwner === owner) {
              this.settingsOwner = null;
              this.subscription = null;
            }
            resolve();
          }
        });
        if (!foundSettings) {
          this.getDefaultMeasureSystem().then(unit => {
            const defaultSettings = new SettingsData();
            defaultSettings.measureUnitSystem = unit;
            this.applyParamSettings(defaultSettings);
            this.firebaseApi.getSettingsDoc().set(JSON.parse(JSON.stringify(defaultSettings)));
          });
        }
      });
    } else {
      Logger.log('initCanvasSettings 3')
      this.getDefaultMeasureSystem().then(unit => {
        if (this.settingsOwner !== owner) {
          // Signed in meanwhile: that account's own settings are what count.
          return;
        }
        const defaultSettings = new SettingsData();
        defaultSettings.measureUnitSystem = unit;
        this.applyParamSettings(defaultSettings);
        this.onNewSettings(defaultSettings);
      });
    }
  }

  applyParamSettings(settings: SettingsData) {
    // const params = this.route.snapshot.queryParams;
    const mapType = this.getParameterByName('maptype');
    const ms = this.getParameterByName('ms');
    const labelSize = this.getParameterByName('labelSize');
    if (Object.values(MapType).includes(mapType)) {
      settings.mapType = mapType;
    }
    // A system only: the enum also holds unit codes ('m', 'ft') and list
    // indexes, and any of those as the system left every measurement blank.
    if ([MeasureUnit.METRIC, MeasureUnit.IMPERIAL, MeasureUnit.OTHER].includes(ms)) {
      settings.measureUnitSystem = ms;
    }
  }

  getParameterByName(name: any): any {
    const url = window.location.href;
    name = name.replace(/[[]]/g, '\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
      const results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/[+]/g, ' '));
  }

  getDefaultMeasureSystem() {
    return new Promise<string>(resolve => {
      this.authApi.getOriginData().then(data => {
        Logger.log('getOriginData', data);
        let unit = MeasureUnit.METRIC;
        if (data && data.country.toLowerCase() === 'us') {
            unit = MeasureUnit.IMPERIAL;
        }
        resolve(unit);
      }).catch(err => {
        resolve(MeasureUnit.METRIC);
      });
    });
  }

  /**
   * Called when the canvas closes. The settings listener is not stopped: it
   * serves the whole app (auth starts it; Profile and Products read it), and
   * stopping it here left the tab deaf to settings changes for the rest of
   * its life, while initCanvasSettings went on finding them "loaded".
   */
  destroy() {
  }

  private stopListening() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  /** A unit list index as the number UnitConverter matches; "0" from older settings, 0 when unreadable. */
  private static unitIndex(value: any): number {
    const index = Number(value);
    return Number.isInteger(index) && index >= 0 ? index : 0;
  }

  setValue(key: string, value: any) {
    if (UNIT_INDEX_KEYS.includes(key)) {
      value = SettingsService.unitIndex(value);
    }
    this.data[key] = value;
    this.unsavedKeys.add(key);
    Logger.log('setValue', this.data);
    SettingsService.sData[key] = value;
    this.onSettingsChanged.next(this.data);
    UnitConverter.setSettings(this.data);

    return this.saveData();
  }

  /**
   * Change the measurement system, and the units that hang off it.
   *
   * `distanceUnit` and `areaUnit` are indexes into a list that is DIFFERENT
   * per system — index 1 means "Are/Hectare" under metric and "Square
   * Yard/Mile" under imperial. Setting the system without resetting them
   * leaves the account pointing at whatever happened to sit at that index in
   * the new list, so the app reports metres in one place and yards in another.
   *
   * Lives here rather than in the canvas sidebar because Profile sets it too,
   * and two call sites doing this by hand is how they drift apart.
   */
  setMeasureSystem(system: string): Promise<unknown> {
    this.data.measureUnitSystem = system;
    SettingsService.sData.measureUnitSystem = system;
    // Index 0 is the plain option in every list — metres, feet — so it is the
    // one choice that means the same thing whichever system was picked.
    this.data.distanceUnit = 0;
    this.data.areaUnit = 0;
    SettingsService.sData.distanceUnit = 0;
    SettingsService.sData.areaUnit = 0;
    ['measureUnitSystem', 'distanceUnit', 'areaUnit'].forEach(key => this.unsavedKeys.add(key));

    this.onSettingsChanged.next(this.data);
    UnitConverter.setSettings(this.data);
    return this.saveData();
  }

  setLocalValue(key, value) {
    this.writeStorage(key, value);
    this.local[key] = value;
    SettingsService.sLocal[key] = value;
    this.onLocalSettingChanged.next({key, value});
  }

  getLocalValue(key, defaultValue?) {
    const value = this.readStorage(key);

    if (value === undefined || value == null || value === '') {
      return defaultValue;
    }
    if (value === 'false') {
      return false;
    }
    if (value === 'true') {
      return true;
    }
    // Not whitespace: Number(' ') and Number('\t') are 0, which turned a saved
    // tab separator into the number 0.
    if (value.trim() !== '' && !isNaN(Number(value))) {
      return Number(value);
    }

    try {
      return JSON.parse(value);
    } catch (e) {
    }
    return value;
  }

  private readStorage(key: string): string {
    if (this.memoryStorage.has(key)) {
      return this.memoryStorage.get(key);
    }
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  private writeStorage(key: string, value: any) {
    try {
      localStorage.setItem(key, value);
      this.memoryStorage.delete(key);
    } catch (e) {
      // Kept for this page only, as text like localStorage would keep it.
      this.memoryStorage.set(key, String(value));
    }
  }

  setValueImmediately(key: string, value: any): Promise<void> {
    const update = {};
    update[key] = value;

    if (CommonUtil.inIframe()) {
      return Promise.resolve();
    }
    return this.firebaseApi.getSettingsDoc().ref.set(update, {merge: true});
  }

  isSnapToPoint(): boolean {
    return this.getLocalValue('snapToPoint', true);
  }

  isPreviewEnabled() {
    return this.getLocalValue('previewCrosshairOp', true);
  }

  isShowCrosshairCoords() {
    return this.getLocalValue('showCrosshairCoordinates', false);
  }

  isAlwaysShowCrosshair() {
    return this.getLocalValue('alwaysShowCrosshair', false);
  }

  setAlwaysShowCrosshair(enabled: boolean) {
    this.setLocalValue('alwaysShowCrosshair', enabled);
  }

  setSnapToPointEnabled(enabled: boolean) {
    this.setLocalValue('snapToPoint', enabled);
  }

  private onNewSettings(data) {
    const incoming = {...data, isDefault: false};
    // A change made here waits 2 s to be saved, and is newer than anything
    // arriving meanwhile; it used to be overwritten by it.
    this.unsavedKeys.forEach(key => delete incoming[key]);
    UNIT_INDEX_KEYS.forEach(key => {
      if (key in incoming) {
        incoming[key] = SettingsService.unitIndex(incoming[key]);
      }
    });
    const unitsBefore = UNIT_KEYS.map(key => this.data[key]);
    Object.assign(this.data, incoming);
    // this.data = data;
    SettingsService.sData = this.data;
    // Before anyone hears of them: the first listeners redrew their labels
    // while UnitConverter still held its own metric defaults.
    UnitConverter.setSettings(this.data);
    this.onSettingsChanged.next(this.data);
    this.onSettingsSaved.next(this.data);
    if (UNIT_KEYS.some((key, i) => this.data[key] !== unitsBefore[i])) {
      // Back in the zone: Firestore may deliver outside it, and the shapes'
      // labels and the measures box are rewritten from a timer.
      this.ngZone.run(() => this.onUnitsReceived.next());
    }
    Logger.log('Setting Changed ', data);
  }

  /**
   * Older settings hold the unit indexes as strings ("0"). They are read as
   * numbers (onNewSettings); this writes them back as numbers too, for the
   * exports and the other devices that read the document as it is stored.
   */
  private saveUnitIndexesAsNumbers(data) {
    const repair = {};
    UNIT_INDEX_KEYS.forEach(key => {
      if (key in data && typeof data[key] !== 'number' && !this.unsavedKeys.has(key)) {
        repair[key] = SettingsService.unitIndex(data[key]);
      }
    });
    if (Object.keys(repair).length) {
      this.firebaseApi.getSettingsDoc().ref.set(repair, {merge: true})
        .catch(err => Logger.error('Could not store the unit settings as numbers', err));
    }
  }

  private saveData() {
    if (CommonUtil.inIframe()) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      this.runSaveTimer(2000).then(() => {
        this.saveNow().then(resolve).catch(reject);
      });
    });
  }

  /**
   * Writes the settings changed here since the last save, and only those.
   * Writing the whole object put this tab's copy of every other setting back
   * over newer values saved since (on another device, or from Products).
   */
  saveNow(): Promise<void> {
    const keys = [...this.unsavedKeys];
    this.unsavedKeys.clear();
    if (!keys.length || !this.firebaseApi.getUserId()) {
      return Promise.resolve();
    }
    const update = {};
    keys.forEach(key => update[key] = this.data[key]);
    return this.firebaseApi.getSettingsDoc().set(JSON.parse(JSON.stringify(update)), {merge: true})
      .catch(err => {
        // Still unsaved: they go out with the next save.
        keys.forEach(key => this.unsavedKeys.add(key));
        throw err;
      });
  }

  private runSaveTimer(duration) {
    return new Promise((resolve, reject) => {
      if (this.saveTimer) {
        clearTimeout(this.saveTimer);
      }
      this.saveTimer = setTimeout(resolve, duration);
    });
  }
}
